import { EntityId, isAnyOf, Middleware } from '@reduxjs/toolkit';
import { isSupportedVariant, getBaseBandByVariant, resolveVariant } from '@ubnt/wifi-calculator/utils';
import {
  devicesCustomClientDeviceInstanceMapSelector,
  devicesDeviceUpdate,
  devicesDeviceVariantSelector,
  devicesDeviceAdd,
  devicesDeviceIdGetter,
  devicesDeviceMapSelector,
  devicesDeviceDefaultInstanceSelector,
} from 'modules/devices/slice';
import { RootState } from 'app/store/reducer';
import { settingsCountrySelector, settingsCeilingHeightSelector } from 'modules/settings/slice';
import { activeElementLinkIdSelector } from 'modules/activeElement/externalSelectors';
import {
  linksClientLinkSelector,
  linksLinkApIdGetter,
  linksLinkByIdSelector,
  linksLinkClientIdGetter,
  linksLinkIdGetter,
  linksLinkIdsByRadioIdMapSelector,
  linksLinkMapSelector,
  linksLinksAddOrUpdate,
  linksLinkSignalGetter,
  StateLink,
} from 'modules/links/slice';
import { isAp, isClient } from 'modules/devices/utils';

export const getLinkReconnects = (state: RootState) => {
  const devices = devicesDeviceMapSelector(state);
  const links = linksLinkMapSelector(state);
  const clientLink = linksClientLinkSelector(state);
  const originalApId = linksLinkApIdGetter(clientLink);
  const currentVariant = devicesDeviceVariantSelector(state, originalApId);

  const country = settingsCountrySelector(state);
  const ceilingHeight = settingsCeilingHeightSelector(state);
  const externalDeviceMap = devicesCustomClientDeviceInstanceMapSelector(state);

  const allDevices = Object.values(devices);
  const apDevices = allDevices.filter(isAp);
  const clientDevices = allDevices.filter(isClient);

  const linkIdsByRadioIdMap = linksLinkIdsByRadioIdMapSelector(state);
  const linkReconnectionArray: StateLink[] = [];

  clientDevices.forEach((clientDevice) => {
    const id = devicesDeviceIdGetter(clientDevice);
    const linkId = linkIdsByRadioIdMap[id][0];
    const link = links[linkId];
    let currentApId = originalApId;
    let currentSignal = linksLinkSignalGetter(
      devices,
      links,
      link,
      currentVariant,
      country,
      externalDeviceMap,
      ceilingHeight
    );

    apDevices.forEach((apDevice) => {
      const apId = devicesDeviceIdGetter(apDevice);
      if (apId === currentApId) {
        return;
      }

      const apDeviceInstance = devicesDeviceDefaultInstanceSelector(state, apId);
      const variant =
        isSupportedVariant(apDeviceInstance.variants, currentVariant) ?
          resolveVariant(apDeviceInstance.variants, currentVariant)
        : devicesDeviceVariantSelector(state, apId);

      if (!isSupportedVariant(apDeviceInstance.variants, variant)) {
        return;
      }

      const apLink: StateLink = { id: linksLinkIdGetter(link), clientId: linksLinkClientIdGetter(link), apId };
      const apSignal = linksLinkSignalGetter(
        devices,
        links,
        apLink,
        variant,
        country,
        externalDeviceMap,
        ceilingHeight
      );

      if (apSignal != null && (currentSignal == null || apSignal > currentSignal)) {
        currentSignal = apSignal;
        currentApId = apId;
      }
    });
    if (currentApId !== originalApId) {
      linkReconnectionArray.push({ id: linkId, apId: currentApId, clientId: id });
    }
  });

  return linkReconnectionArray;
};

const isReconnectAction = isAnyOf(devicesDeviceUpdate, devicesDeviceAdd);

export const linksMiddleware: Middleware<unknown, RootState> = (store) => (next) => (action) => {
  if (isReconnectAction(action)) {
    const result = next(action);
    const hasPosition = 'changes' in action.payload && 'position' in action.payload.changes;

    if (action.type === devicesDeviceUpdate.toString() && !hasPosition) {
      return result;
    }

    const state = store.getState();
    const dispatch = store.dispatch;
    const activeElementLinkId = activeElementLinkIdSelector(state);
    const activeElementLink = linksLinkByIdSelector(state, activeElementLinkId);
    const activeApId = linksLinkApIdGetter(activeElementLink);
    const variant = devicesDeviceVariantSelector(state, activeApId);
    const reconnects = getLinkReconnects(state);

    if (reconnects.length > 0) {
      const updateMap = reconnects.reduce(
        (map, link) => ({ ...map, [linksLinkIdGetter(link)]: link }),
        {} as Record<EntityId, StateLink>
      );

      reconnects.forEach((reconnect) => {
        const id = linksLinkApIdGetter(reconnect);
        const defaultApInstance = devicesDeviceDefaultInstanceSelector(state, id);
        const currentVariant = devicesDeviceVariantSelector(state, id);
        const newVariant =
          (
            getBaseBandByVariant(variant!) !== getBaseBandByVariant(currentVariant!) &&
            isSupportedVariant(defaultApInstance.variants, variant)
          ) ?
            resolveVariant(defaultApInstance.variants, variant)
          : null;

        if (newVariant != null) {
          dispatch(devicesDeviceUpdate({ id, changes: { variant: newVariant } }));
        }
      });

      dispatch(linksLinksAddOrUpdate(updateMap));
    }

    return result;
  }

  return next(action);
};
